import { Directive, Input, HostListener, ComponentRef, ElementRef, OnDestroy } from '@angular/core';
import { Overlay, OverlayRef, OverlayPositionBuilder } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { AML_SOURCES, DVS_SOURCES, IdvTooltipComponent } from './idv-tooltip.component';
import { IdentityVerificationDetails } from '../../interfaces/identity-verification-details/identity-verification-details';
import { eIdentityVerificationResult } from '../../enums';

@Directive({
  selector: '[appIdvTooltip]',
  standalone: false
})
export class IdvTooltipDirective implements OnDestroy {
  @Input('appIdvTooltip') idvDetails?: IdentityVerificationDetails;

  private overlayRef?: OverlayRef;
  private hideTimeout?: ReturnType<typeof setTimeout>;
  private fadeTimeout?: ReturnType<typeof setTimeout>;

  constructor(
    private overlay: Overlay,
    private positionBuilder: OverlayPositionBuilder,
    private elementRef: ElementRef
  ) { }

  @HostListener('mouseenter')
  show() {
    clearTimeout(this.hideTimeout);
    clearTimeout(this.fadeTimeout);

    // remove fade-out class if it's mid-animation
    const element = this.overlayRef?.overlayElement.querySelector('.idv-tooltip');
    element?.classList.remove('fade-out');

    if (this.overlayRef?.hasAttached()) return; // fixes the double-render too

    const positionStrategy = this.positionBuilder
      .flexibleConnectedTo(this.elementRef)
      .withPositions([
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 }
      ]);

    const aml = this.idvDetails?.sources?.filter(s => AML_SOURCES.includes(s.name) && !(s.status == eIdentityVerificationResult.NA)) || [];
    const dvs = this.idvDetails?.sources?.filter(s => DVS_SOURCES.includes(s.name) && !(s.status == eIdentityVerificationResult.NA)) || [];

    const dataColumns = Math.max(aml.length, dvs.length);
    const width = 150 + (200 * dataColumns);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: false,
      width: `${width}px`
    });

    const portal = new ComponentPortal(IdvTooltipComponent);
    const ref: ComponentRef<IdvTooltipComponent> = this.overlayRef.attach(portal);
    ref.instance.idvDetails = this.idvDetails;
    ref.instance.tables = ref.instance.buildTables(); // call directly
    ref.changeDetectorRef.detectChanges();

    setTimeout(() => {
      const element = this.overlayRef?.overlayElement.querySelector('.idv-tooltip');
      element?.classList.remove('fade-out');
    });
  }

  @HostListener('mouseleave', ['$event'])
  hide(event: MouseEvent) {
    if (this.elementRef.nativeElement.contains(event.relatedTarget as Node)) {
      return;
    }

    const element = this.overlayRef?.overlayElement.querySelector('.idv-tooltip');
    this.hideTimeout = setTimeout(() => {
      element?.classList.add('fade-out');
      this.fadeTimeout = setTimeout(() => this.overlayRef?.detach(), 150);
    }, 300);
  }

  ngOnDestroy() {
    clearTimeout(this.hideTimeout);
    clearTimeout(this.fadeTimeout);
    this.overlayRef?.dispose();
  }
}
